/**
 * Display helpers shared by the Price Rules and Condition Discounts tabs.
 * Rules have no display name of their own — their identity is the set of
 * match criteria, so these helpers turn criteria into readable summaries.
 */
import type { PricingRule, PricingType } from '../pricingRules/types';

export const SOURCE_COLUMN_LABELS: Record<string, string> = {
  price: 'Price',
  msrp_price: 'MSRP',
  last_known_retail_price: 'Last known retail',
  last_known_outlet_price: 'Last known outlet',
  absolute_price: 'Absolute price',
};

export const sourceColumnLabel = (sourceColumn: string | null | undefined): string =>
  (sourceColumn ? SOURCE_COLUMN_LABELS[sourceColumn] ?? sourceColumn : '—');

/** "80%" for percentage rules, "$19.99" for absolute_price rules. */
export const formatRuleDiscount = (rule: PricingRule, currencySymbol: string): string => {
  const discount = rule.discount ?? 0;
  if (rule.sourceColumn === 'absolute_price') {
    return `${currencySymbol}${(discount / 100).toFixed(2)}`;
  }
  return `${discount}%`;
};

/** "−15%" for condition discounts; zero renders without the minus sign. */
export const formatConditionDiscount = (rule: PricingRule): string => {
  const discount = rule.discount ?? 0;
  return discount > 0 ? `−${discount}%` : '0%';
};

/** The facet tag/name pairs a rule matches on, in priority order. */
export const facetPairs = (rule: PricingRule): { tag: string, name: string | null }[] => {
  const pairs: { tag: string, name: string | null }[] = [];
  if (rule.tag) pairs.push({ tag: rule.tag, name: rule.name ?? null });
  if (rule.secondaryTag) pairs.push({ tag: rule.secondaryTag, name: rule.secondaryName ?? null });
  if (rule.tertiaryTag) pairs.push({ tag: rule.tertiaryTag, name: rule.tertiaryName ?? null });
  return pairs;
};

/**
 * One-line human summary of everything a rule matches on, e.g.
 * "Women's · Shoes · brand: Acme". Rules with no criteria match everything.
 * conditionLabels maps condition codes to display names (from the conditions
 * resource) so summaries read "Very Good" instead of "very_good".
 */
export const ruleScopeSummary = (
  rule: PricingRule,
  pricingType: PricingType,
  conditionLabels?: Record<string, string>,
): string => {
  const parts: string[] = [];
  if (pricingType === 'condition' && rule.condition) {
    parts.push(conditionLabels?.[rule.condition] ?? rule.condition);
  }
  if (rule.department) parts.push(rule.department);
  if (rule.category) parts.push(rule.category);
  if (rule.merchantCategory) parts.push(rule.merchantCategory);
  if (rule.upc) parts.push(`UPC ${rule.upc}`);
  parts.push(...facetPairs(rule).map(({ tag, name }) => (name ? `${tag}: ${name}` : tag)));
  if (!parts.length) {
    return pricingType === 'condition' ? 'All conditions, all items' : 'All items';
  }
  return parts.join(' · ');
};

/** Count of populated match criteria — the caption line under the summary. */
export const ruleCriteriaCount = (rule: PricingRule, pricingType: PricingType): number => {
  const fields = [
    rule.department,
    rule.category,
    rule.merchantCategory,
    rule.upc,
    rule.tag,
    rule.secondaryTag,
    rule.tertiaryTag,
  ];
  if (pricingType === 'condition') fields.push(rule.condition);
  return fields.filter(Boolean).length;
};
